import { getEnvVar } from "@/common/utils/environment";
import { createClient } from "@supabase/supabase-js";
import { Capacitor } from "@capacitor/core";
import { debounce } from "@/common/utils/debounce";
import { Storage } from "@/common/utils/storage";
import { Database } from "@/common/types/database.types";
import posthog from "posthog-js";
export const STORAGE_KEY = "sb.auth.token";
// Per-user idle auth timeout (minutes), mirrored from user.session_timeout_minutes.
// When present, it overrides both the app default and remember-me.
export const SESSION_TIMEOUT_MINUTES_KEY = "session_timeout_minutes";

const REMEMBER_ME_TIME = 7 * 24 * 60 * 60 * 1000;

/**
 * Mirrors the user's per-user idle timeout into local storage so the low-level
 * session storage below can enforce it. Call with the value from
 * user.getUser (session_timeout_minutes); null/0 clears the override.
 */
export const setStoredSessionTimeoutMinutes = async (
  minutes: number | null,
) => {
  if (minutes && minutes > 0) {
    await Storage.setItem(SESSION_TIMEOUT_MINUTES_KEY, String(minutes));
  } else {
    await Storage.removeItem(SESSION_TIMEOUT_MINUTES_KEY);
  }
};
const createSupabaseClient = () => {
  const expiryMinutes = parseInt(
    getEnvVar("VITE_PUBLIC_TOKEN_EXPIRY_MINUTES") || "240",
  );

  const isNative = Capacitor.isNativePlatform();

  const client = createClient<Database>(
    getEnvVar("VITE_PUBLIC_SUPABASE_URL"),
    getEnvVar("VITE_PUBLIC_SUPABASE_ANON_KEY"),
    {
      auth: {
        storageKey: STORAGE_KEY,
        storage: {
          getItem: async (key) => {
            const item = await Storage.getItem(key);
            if (!item) {
              return null;
            }

            const { value, timestamp } = JSON.parse(item);

            if (key === STORAGE_KEY) {
              const rememberMe =
                (await Storage.getItem("remember_me_enabled")) === "true";

              // A per-user timeout (e.g. shorter for admins) overrides both the
              // app default and remember-me, and is enforced even when the
              // default timeout is disabled (expiryMinutes === 0).
              const perUserRaw = await Storage.getItem(
                SESSION_TIMEOUT_MINUTES_KEY,
              );
              const perUserMinutes = perUserRaw
                ? parseInt(perUserRaw, 10)
                : NaN;
              const hasPerUser =
                Number.isFinite(perUserMinutes) && perUserMinutes > 0;

              const expiryTime = hasPerUser
                ? perUserMinutes * 60 * 1000
                : rememberMe
                  ? REMEMBER_ME_TIME
                  : expiryMinutes * 60 * 1000;

              if (
                !isNative &&
                (hasPerUser || expiryMinutes > 0) &&
                Date.now() - timestamp > expiryTime
              ) {
                posthog.capture("token_expired_sign_out");
                return null;
              }
            }

            return value;
          },
          setItem: async (key, value) => {
            const item = JSON.stringify({
              value,
              timestamp: Date.now(),
            });
            await Storage.setItem(key, item);
          },
          removeItem: async (key) => {
            await Storage.removeItem(key);
          },
        },
      },
    },
  );
  return client;
};

export const supabase = createSupabaseClient();

export const getAccessToken = async (): Promise<string | null> => {
  const token = await supabase.auth.getSession();

  return token.data.session?.access_token ?? null;
};
// Debounced function to update token expiry timestamp on user interaction
const updateTokenExpiry = debounce(async () => {
  const token = await Storage.getItem(STORAGE_KEY);
  if (token) {
    const parsed = JSON.parse(token);
    parsed.timestamp = Date.now();
    Storage.setItem(STORAGE_KEY, JSON.stringify(parsed));
  }
}, 1000);

// Add event listeners for user interaction
["click", "keydown", "mousemove", "touchstart"].forEach((eventType) => {
  document.addEventListener(eventType, updateTokenExpiry, { passive: true });
});
